/**
 * useAdminAuth — checks if the current Better Auth session belongs to a
 * platform admin. The check is a lightweight GET /api/admin/platform-stats
 * that returns 403 if not admin. On success, the user is confirmed admin.
 */

import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';

export type AdminAuthState = 'loading' | 'unauthenticated' | 'not_admin' | 'admin';

interface AdminAuthCtx {
  state: AdminAuthState;
}

const Ctx = createContext<AdminAuthCtx>({ state: 'loading' });

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AdminAuthState>('loading');

  useEffect(() => {
    fetch('/api/admin/platform-stats', { credentials: 'include' })
      .then((res) => {
        if (res.status === 401) {
          setState('unauthenticated');
        } else if (res.status === 403) {
          setState('not_admin');
        } else if (res.ok) {
          setState('admin');
        } else {
          setState('unauthenticated');
        }
      })
      .catch(() => {
        setState('unauthenticated');
      });
  }, []);

  return <Ctx.Provider value={{ state }}>{children}</Ctx.Provider>;
}

export function useAdminAuth(): AdminAuthCtx {
  return useContext(Ctx);
}
